// WelcomeScreen.tsx — a one-shot confirmation shown right after a fresh sign-in,
// reflecting how access resolved:
//   trial          — free plan
//   member         — paid / returning membership
//   org            — organisation-provided membership (coupon OR domain), names the org
//   org_unavailable— recognised org, but its licence is spent/expired/cancelled
// Set by useAuth.confirmOtp; dismissed on tap. House style: no emoji, no em-dash.
import React from 'react';
import { Display, Body, Eyebrow, Btn } from '@/components/ui';
import { useAuth, type Welcome } from '@/hooks/useAuth';
import { useIsDesktop } from '@/hooks/useIsDesktop';
import { SHOW_PURCHASE, SHOW_IAP } from '@/config/supabase';
import { ONBOARDING_2026 } from '@/config/features';

/** Renders **bold** runs inside a paragraph, so the copy below stays readable. */
function rich(line: string): React.ReactNode[] {
  return line.split(/(\*\*[^*]+\*\*)/g).filter(Boolean).map((part, i) =>
    part.startsWith('**') && part.endsWith('**')
      ? <strong key={i} style={{ color: 'var(--ink)', fontWeight: 600 }}>{part.slice(2, -2)}</strong>
      : <React.Fragment key={i}>{part}</React.Fragment>,
  );
}

/** `body` is one paragraph; `paras` is several. A screen supplies one or the other. */
function copyFor(w: Welcome): { eyebrow?: string; title: string; body?: string; paras?: string[]; cta: string } {
  if (w.kind === 'org') {
    const org = w.orgName || 'your organisation';
    const term = w.termMonths ? `${w.termMonths} months` : 'the months ahead';
    return {
      eyebrow: `Provided by ${org}`,
      title: 'Welcome to ReNurture',
      body: `Your membership is provided by ${org}. Everything is unlocked for the next ${term}. There is no rush. Move through the programme at the pace that feels right for you.`,
      cta: 'Enter',
    };
  }
  if (w.kind === 'member') {
    return {
      title: 'Welcome to your membership',
      body: 'Everything is unlocked. There is no rush. Move through the programme at the pace that feels right for you.',
      cta: 'Enter',
    };
  }
  if (w.kind === 'org_unavailable') {
    const org = w.orgName || 'your organisation';
    const full = w.reason === 'seats_full';
    return {
      eyebrow: w.orgName ? `From ${w.orgName}` : undefined,
      title: full ? 'All places are currently taken' : 'This licence has ended',
      body: full
        ? `It looks like all of ${org}'s ReNurture places are in use right now. You are set up with a free account so you can still begin. Please ask ${org}, or email support@renurture.org, and we will help.`
        : `It looks like ${org}'s ReNurture licence has come to an end. You are set up with a free account so you can still begin. Please ask ${org}, or email support@renurture.org, and we will help.`,
      cta: 'Continue',
    };
  }
  // ONBOARDING_2026: the same three promises, broken into paragraphs with the key
  // phrases weighted. As one block they were a wall a new user skimmed past, and the
  // one thing they most need to take away (this is free, and part of it stays free)
  // was buried in the middle of it.
  if (ONBOARDING_2026) {
    return {
      eyebrow: '10-day free trial',
      title: 'Welcome to ReNurture',
      paras: [
        'Your **free 10-day trial** unlocks every feature: all the tools, the meditations, and the opening chapters of the programme.',
        '**There is no rush.** Move at the pace that feels right for you.',
        'The **self-compassion course is yours to keep, free**, as our gift, even after the trial ends.',
      ],
      cta: 'Start my trial',
    };
  }
  return {
    eyebrow: '10-day free trial',
    title: 'Welcome to ReNurture',
    // Native apps carry no purchasing, so the trial welcome there drops the "become a
    // member" line; the web build keeps the full version.
    body: SHOW_PURCHASE || SHOW_IAP
      ? 'Your free 10-day trial unlocks every feature: all the tools, the meditations, and the opening chapters of the programme. There is no rush, so move at the pace that feels right for you. Become a member any time for the full programme, and the self-compassion course is yours to keep, free, as our gift, even after the trial ends.'
      : 'Your free 10-day trial unlocks every feature: all the tools, the meditations, and the opening chapters of the programme. There is no rush, so move at the pace that feels right for you. The self-compassion course is yours to keep, free, as our gift, even after the trial ends.',
    cta: 'Start my trial',
  };
}

export function WelcomeScreen() {
  const auth = useAuth();
  const isDesktop = useIsDesktop();
  if (!auth.welcome) return null;
  const c = copyFor(auth.welcome);
  return (
    <div role="dialog" aria-modal="true" aria-label="Welcome" style={overlay}>
      <div style={{ ...panel, maxWidth: isDesktop ? 480 : 420 }}>
        {c.eyebrow && <Eyebrow style={{ marginBottom: 12 }}>{c.eyebrow}</Eyebrow>}
        <Display text={c.title} size={30} weight={400} style={{ marginBottom: 14 }} />
        {c.body && <Body size={15} style={{ marginBottom: 28, lineHeight: 1.66 }}>{c.body}</Body>}
        {c.paras?.map((para, i, arr) => (
          <Body key={i} size={15} style={{ marginBottom: i === arr.length - 1 ? 30 : 16, lineHeight: 1.7 }}>
            {rich(para)}
          </Body>
        ))}
        <Btn full size="lg" onClick={() => auth.dismissWelcome()}>{c.cta}</Btn>
      </div>
    </div>
  );
}

const overlay: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  zIndex: 1000,
  background: 'var(--cream, #F7F5F0)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '32px 24px',
  paddingTop: 'calc(var(--safe-top) + 32px)',
  paddingBottom: 'calc(var(--safe-bottom) + 32px)',
};
const panel: React.CSSProperties = {
  width: '100%',
  maxWidth: 420,
  textAlign: 'left',
};
